import { useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import type { TeamRole } from "../../lib/types";
import { BILL_CHIP_CLASS, BOOKS_PAYOUT_BILL_SELECT, billChipText, billChipTone, normalizeBill, type BooksPayoutBill } from "../../lib/booksPayoutBills";

/**
 * Canvasser Payouts — weekly hourly + per-app pay for canvassers (1099).
 * Spec: memory/features/canvasser-program.md (Ship 3, May 22, 2026).
 *
 * Surfaces on Chad's Commission board so canvasser pay is approved each week
 * alongside RM/TL/ASM commissions. Source rows are generated every Monday by
 * the `canvasser_payouts_weekly` cron (fn `canvasser_accrue_weekly_payouts`).
 *
 *   Pending tab   → status='pending'  → Chad approves
 *   Approved tab  → status='approved' → Shannon marks paid (→ QBO 1099 bill, future)
 *   Paid tab      → status='paid'     → read-only history
 *
 * Oct 8, 2026 — approving goes through `canvasser_payouts_approve`: the row
 * is flagged approved and ONE Books bill per canvasser is made for the batch
 * (`books_bill_id`); Shannon pays it in Books and the pull marks the row paid
 * ('books:<method>'). Rows with a bill show the chip and no Mark paid; rows
 * approved before Oct 8 (no bill) keep Shannon's Mark paid here.
 *
 * Oct 26, 2026 — each week shows its stats from `canvasser_week_detail`:
 * hours, appointments set (submitted and not rejected) against the minimum
 * of 1 per 2 hours (finance_settings canvasser_min_appts_per_hour), and every
 * canvassing session, flagged when it ended automatically after sitting idle
 * (`canvasser_close_idle_sessions`). Chad can correct a pending week's hours
 * with a note (`canvasser_payout_adjust_hours`, audited) before approving.
 * Weeks with no hours, appointments or pay are folded into one line: there is
 * nothing to approve in them.
 */

type PayoutStatus = "pending" | "approved" | "paid";

interface PayoutRow {
  id: string;
  canvasser_id: string;
  period_start: string; // YYYY-MM-DD (Monday)
  period_end: string;   // exclusive
  hours_worked: number | string;
  hourly_rate: number | string;
  hourly_amount: number | string;
  apps_qualified: number;
  apps_unqualified: number;
  per_app_amount: number | string;
  additional_adjustment: number | string;
  adjustment_note: string | null;
  total: number | string;
  status: PayoutStatus;
  approved_at: string | null;
  paid_at: string | null;
  canvasser: { full_name: string } | { full_name: string }[] | null;
  hours_recorded?: number | string | null;
  hours_adjust_note?: string | null;
  hours_adjusted_at?: string | null;
  /** Oct 8, 2026 — the Books bill that carries this week, when approved through the RPC. */
  books_bill_id?: string | null;
  bill?: BooksPayoutBill | { [k: string]: unknown }[] | { [k: string]: unknown } | null;
}

function billOf(r: PayoutRow): BooksPayoutBill | null {
  const b = Array.isArray(r.bill) ? r.bill[0] : r.bill;
  return b ? normalizeBill(b as Record<string, unknown>) : null;
}

interface WeekSession {
  id: string;
  started_at: string;
  ended_at: string | null;
  hours: number;
  open: boolean;
  auto_closed: boolean;
  pings: number | null;
  knocks: number | null;
  contacts: number | null;
  apps: number;
}

interface WeekDetail {
  hours: number;
  hours_recorded: number;
  sessions: WeekSession[];
  apps_set: number;
  apps_approved: number;
  apps_rejected: number;
  apps_pending: number;
  min_per_hour: number;
  apps_per_2h: number | null;
  needed: number;
  below_min: boolean;
}

interface AppRow {
  id: string;
  property_street: string | null;
  property_city: string | null;
  insurance_company: string | null;
}

function canApprove(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "sales_director" || role === "admin" || role === "director_of_operations";
}
function canMarkPaid(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "finance" || role === "admin";
}

// Date-only string → friendly label without the UTC-midnight off-by-one trap.
function fmtDate(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function num(v: number | string | null | undefined): number {
  return typeof v === "number" ? v : Number(v) || 0;
}
// Session times read in Eastern, the team's clock.
function etDayTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function etTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
}
function hrs(n: number): string {
  return (Math.round(n * 100) / 100).toFixed(2);
}
// Nothing to approve: no hours, no appointments, no pay.
function isEmptyWeek(r: PayoutRow): boolean {
  return num(r.hours_worked) === 0 && num(r.total) === 0 && r.apps_qualified + r.apps_unqualified === 0;
}

function canvasserName(r: PayoutRow): string {
  const c = r.canvasser;
  const obj = Array.isArray(c) ? c[0] : c;
  return obj?.full_name ?? "Canvasser";
}

const STATUS_FOR_TAB: Record<"pending" | "approved" | "paid", PayoutStatus> = {
  pending: "pending",
  approved: "approved",
  paid: "paid",
};

export default function CanvasserPayoutsSection({
  tab,
  role,
  isSuperUser,
  onChanged,
}: {
  tab: "pending" | "approved" | "paid";
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onChanged: () => void;
}) {
  const { member } = useAuth();
  const status = STATUS_FOR_TAB[tab];
  const [rows, setRows] = useState<PayoutRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [apps, setApps] = useState<Record<string, AppRow[]>>({});
  const [details, setDetails] = useState<Record<string, WeekDetail | "error">>({});
  const [sessionsOpen, setSessionsOpen] = useState<string | null>(null);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [adjHours, setAdjHours] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [adjErr, setAdjErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("canvasser_payouts")
      .select(
        `*, canvasser:team_roster!canvasser_payouts_canvasser_id_fkey(full_name), bill:books_payout_bills(${BOOKS_PAYOUT_BILL_SELECT})`,
      )
      .eq("status", status)
      .order("period_start", { ascending: false });
    if (error) { setErr(formatSupabaseError(error)); return; }
    setErr(null);
    const list = (data ?? []) as PayoutRow[];
    setRows(list);
    // Stats for every week with something in it (a handful of rows).
    const results = await Promise.all(
      list.filter((r) => !isEmptyWeek(r)).map(async (r) => {
        const { data: d, error: e } = await supabase.rpc("canvasser_week_detail", { p_payout_id: r.id });
        return [r.id, e || !d ? "error" : (d as WeekDetail)] as const;
      }),
    );
    setDetails(Object.fromEntries(results));
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useRealtimeTable("canvasser_payouts", load);

  async function approve(id: string) {
    setActing((p) => new Set(p).add(id));
    // Oct 8, 2026 — the RPC flags the row and makes the canvasser's Books bill.
    const { error } = await supabase.rpc("canvasser_payouts_approve", { p_ids: [id] });
    setActing((p) => { const n = new Set(p); n.delete(id); return n; });
    if (error) { setErr(formatSupabaseError(error)); return; }
    onChanged();
    load();
  }

  async function saveHours(id: string) {
    const h = Number(adjHours);
    if (!Number.isFinite(h) || h < 0 || h > 100) { setAdjErr("Enter hours between 0 and 100."); return; }
    if (adjNote.trim().length < 5) { setAdjErr("Say why the hours changed."); return; }
    setActing((p) => new Set(p).add(id));
    const { error } = await supabase.rpc("canvasser_payout_adjust_hours", { p_payout_id: id, p_hours: h, p_note: adjNote.trim() });
    setActing((p) => { const n = new Set(p); n.delete(id); return n; });
    if (error) { setAdjErr(formatSupabaseError(error)); return; }
    setAdjusting(null);
    setAdjErr(null);
    onChanged();
    load();
  }

  async function markPaid(id: string) {
    setActing((p) => new Set(p).add(id));
    const { error } = await supabase
      .from("canvasser_payouts")
      .update({ status: "paid", paid_by: member?.id ?? null, paid_at: new Date().toISOString() })
      .eq("id", id);
    setActing((p) => { const n = new Set(p); n.delete(id); return n; });
    if (error) { setErr(formatSupabaseError(error)); return; }
    onChanged();
    load();
  }

  async function toggleApps(r: PayoutRow) {
    if (expanded === r.id) { setExpanded(null); return; }
    setExpanded(r.id);
    if (!apps[r.id]) {
      const { data } = await supabase
        .from("pending_canvas_appointments")
        .select("id, property_street, property_city, insurance_company")
        .eq("submitted_by", r.canvasser_id)
        .eq("status", "approved")
        .gte("submitted_at", r.period_start + "T00:00:00")
        .lt("submitted_at", r.period_end + "T00:00:00")
        .order("submitted_at", { ascending: true });
      setApps((prev) => ({ ...prev, [r.id]: (data ?? []) as AppRow[] }));
    }
  }

  const visible = rows?.filter((r) => !isEmptyWeek(r)) ?? null;
  const empty = rows?.filter(isEmptyWeek) ?? [];
  const emptyNames = [...new Set(empty.map(canvasserName))];

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-bardin-ink">🚪 Canvasser Pay</h2>
          <p className="text-xs text-slate-500">
            Weekly $35/hr + $10 per app with insurance confirmed, $5 unconfirmed or unqualified (1099). {tab === "pending" ? "Approve to release." : tab === "approved" ? "Mark paid → QBO bill." : "History."}
          </p>
        </div>
        {visible && visible.length > 0 && (
          <div className="text-right">
            <div className="text-sm font-semibold text-bardin-ink">
              {fmtUSD(visible.reduce((s, r) => s + num(r.total), 0))}
            </div>
            <div className="text-xs text-slate-500">{visible.length} {visible.length === 1 ? "payout" : "payouts"}</div>
          </div>
        )}
      </div>

      {err && (
        <div className="mb-2 text-xs bg-red-50 text-red-700 rounded px-3 py-2">{err}</div>
      )}

      {visible === null ? (
        <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-400">Loading canvasser pay…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          No canvasser payouts {tab === "pending" ? "awaiting approval" : tab}.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((r) => {
            const d = details[r.id];
            const det = d && d !== "error" ? d : null;
            const recorded = r.hours_recorded != null ? num(r.hours_recorded) : null;
            const totalApps = r.apps_qualified + r.apps_unqualified;
            const adj = num(r.additional_adjustment);
            return (
              <div key={r.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{canvasserName(r)}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700 font-medium capitalize">{r.status}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Week of {fmtDate(r.period_start)} – {fmtDate(new Date(new Date(r.period_end + "T12:00:00").getTime() - 86400000).toISOString().slice(0, 10))}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {num(r.hours_worked).toFixed(2)} hrs · {totalApps} {totalApps === 1 ? "app" : "apps"}
                      {totalApps > 0 ? ` (${r.apps_qualified} confirmed · ${r.apps_unqualified} unconfirmed)` : ""}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(num(r.total))}</div>
                    <div className="text-xs text-slate-500">total</div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Box label={`Hourly (${num(r.hours_worked).toFixed(2)}h × $${num(r.hourly_rate)})`} value={num(r.hourly_amount)} />
                  <Box label="Per-app" value={num(r.per_app_amount)} />
                  <Box label="Adjustment" value={adj} note={r.adjustment_note ?? undefined} muted={adj === 0} />
                </div>

                {recorded != null && recorded !== num(r.hours_worked) && (
                  <div className="mt-2 text-xs rounded-lg bg-sky-50 text-sky-800 px-3 py-1.5">
                    Hours adjusted from {hrs(recorded)} to {hrs(num(r.hours_worked))}
                    {r.hours_adjust_note ? `: ${r.hours_adjust_note}` : ""}
                    {r.hours_adjusted_at ? ` (${fmtDate(r.hours_adjusted_at.slice(0, 10))})` : ""}
                  </div>
                )}

                {d === "error" && (
                  <div className="mt-2 text-xs text-red-600">Couldn't load this week's sessions.</div>
                )}
                {det && (
                  <>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <Stat label="Hours" value={hrs(det.hours)} />
                      <Stat
                        label="Appts set"
                        value={String(det.apps_set)}
                        hint={`${det.apps_approved} approved · ${det.apps_pending} waiting · ${det.apps_rejected} rejected`}
                      />
                      <Stat label="Per 2 hrs" value={det.apps_per_2h != null ? det.apps_per_2h.toFixed(2) : "—"} hint="minimum 1.00" />
                    </div>
                    {det.hours < 1 ? (
                      <div className="mt-2 text-xs rounded-lg bg-slate-50 text-slate-500 px-3 py-1.5">Under 1 hour logged this week.</div>
                    ) : det.below_min ? (
                      <div className="mt-2 text-xs rounded-lg bg-red-50 text-red-700 px-3 py-1.5">
                        <strong>Below the minimum:</strong> {det.apps_set} {det.apps_set === 1 ? "appointment" : "appointments"} set in {hrs(det.hours)} hrs.
                        The minimum is 1 per 2 hours, so {det.needed} for this week. Coach before next week.
                      </div>
                    ) : (
                      <div className="mt-2 text-xs rounded-lg bg-emerald-50 text-emerald-700 px-3 py-1.5">
                        Meets the minimum of 1 appointment per 2 hours.
                      </div>
                    )}
                    {det.sessions.length > 0 && (
                      <button
                        onClick={() => setSessionsOpen(sessionsOpen === r.id ? null : r.id)}
                        className="mt-2 mr-4 text-xs text-bardin-orange hover:underline"
                      >
                        {sessionsOpen === r.id ? "▾ Hide sessions" : `▸ View ${det.sessions.length} ${det.sessions.length === 1 ? "session" : "sessions"}`}
                      </button>
                    )}
                    {sessionsOpen === r.id && (
                      <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1.5">
                        {det.sessions.map((x) => (
                          <div key={x.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="font-medium text-bardin-ink">
                              {etDayTime(x.started_at)} – {x.ended_at ? etTime(x.ended_at) : "still on"}
                            </span>
                            <span>{hrs(x.hours)} h</span>
                            <span className="text-slate-400">
                              {x.knocks ?? 0} doors · {x.contacts ?? 0} contacts · {x.apps} {x.apps === 1 ? "appt" : "appts"}
                            </span>
                            {x.auto_closed && <Chip tone="amber">ended automatically (idle)</Chip>}
                            {x.open && <Chip tone="rose">still open</Chip>}
                            {x.hours >= 10 && <Chip tone="amber">10+ hrs</Chip>}
                            {x.hours >= 1 && (x.pings ?? 0) < x.hours * 2 && <Chip tone="slate">few GPS pings</Chip>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {totalApps > 0 && (
                  <button
                    onClick={() => toggleApps(r)}
                    className="mt-2 text-xs text-bardin-orange hover:underline"
                  >
                    {expanded === r.id ? "▾ Hide apps" : `▸ View ${totalApps} ${totalApps === 1 ? "app" : "apps"}`}
                  </button>
                )}
                {expanded === r.id && (
                  <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1">
                    {(apps[r.id] ?? []).length === 0 ? (
                      <div className="text-slate-400">Loading…</div>
                    ) : (
                      (apps[r.id] ?? []).map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-2">
                          <span className="truncate">{a.property_street ?? "—"}{a.property_city ? `, ${a.property_city}` : ""}</span>
                          <span className="text-slate-400 flex-shrink-0">{a.insurance_company?.trim() || "no carrier"}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {tab === "pending" && canApprove(role, isSuperUser) && adjusting === r.id && (
                  <div className="mt-3 rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="text-xs font-medium text-bardin-ink">Adjust hours for this week</div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        max="100"
                        value={adjHours}
                        onChange={(e) => setAdjHours(e.target.value)}
                        className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                        aria-label="Hours"
                      />
                      <input
                        type="text"
                        value={adjNote}
                        onChange={(e) => setAdjNote(e.target.value)}
                        placeholder="Why (e.g. left session on during lunch)"
                        className="flex-1 min-w-[12rem] rounded border border-slate-300 px-2 py-1 text-sm"
                        aria-label="Reason"
                      />
                    </div>
                    {adjErr && <div className="text-xs text-red-600">{adjErr}</div>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveHours(r.id)}
                        disabled={acting.has(r.id)}
                        className="px-3 py-1 text-xs font-medium rounded text-white bg-bardin-teal hover:opacity-90 disabled:opacity-50"
                      >
                        {acting.has(r.id) ? "Saving…" : `Save · ${fmtUSD((Number(adjHours) || 0) * num(r.hourly_rate))} hourly`}
                      </button>
                      <button onClick={() => { setAdjusting(null); setAdjErr(null); }} className="px-3 py-1 text-xs rounded border border-slate-300 text-slate-600">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {tab === "pending" && canApprove(role, isSuperUser) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => approve(r.id)}
                      disabled={acting.has(r.id) || num(r.total) < 0.005}
                      title={num(r.total) < 0.005 ? "Nothing to pay this week" : undefined}
                      className="px-4 py-1.5 text-xs font-medium rounded text-white bg-bardin-orange hover:opacity-90 disabled:opacity-50"
                    >
                      {acting.has(r.id) ? "Approving…" : "Approve payout"}
                    </button>
                    {adjusting !== r.id && (
                      <button
                        onClick={() => { setAdjusting(r.id); setAdjHours(hrs(num(r.hours_worked))); setAdjNote(""); setAdjErr(null); }}
                        className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                      >
                        Adjust hours
                      </button>
                    )}
                  </div>
                )}
                {tab === "approved" && r.books_bill_id && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      title={billOf(r) ? billChipText(billOf(r)!) : "Bill in Books"}
                      className={"inline-block text-[11px] font-semibold px-2 py-1 rounded " + BILL_CHIP_CLASS[billOf(r) ? billChipTone(billOf(r)!) : "sent"]}
                    >
                      {billOf(r) ? billChipText(billOf(r)!) : "Bill in Books"}
                    </span>
                    <span className="text-[11px] text-slate-400 italic">Paid in Books; the row reads paid once Books reports back.</span>
                  </div>
                )}
                {tab === "approved" && !r.books_bill_id && canMarkPaid(role, isSuperUser) && (
                  <div className="mt-3">
                    <button
                      onClick={() => markPaid(r.id)}
                      disabled={acting.has(r.id)}
                      className="px-4 py-1.5 text-xs font-medium rounded text-white bg-emerald-600 hover:opacity-90 disabled:opacity-50"
                    >
                      {acting.has(r.id) ? "Marking…" : "Mark paid"}
                    </button>
                  </div>
                )}
                {tab === "paid" && r.paid_at && (
                  <div className="mt-2 text-xs text-slate-400">Paid {fmtDate(r.paid_at.slice(0, 10))}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {empty.length > 0 && (
        <p className="mt-2 text-xs text-slate-400">
          {empty.length} {empty.length === 1 ? "week" : "weeks"} with no canvassing sessions logged
          {emptyNames.length ? ` (${emptyNames.join(", ")})` : ""}: nothing to approve.
        </p>
      )}
    </div>
  );
}

function Box({ label, value, note, muted }: { label: string; value: number; note?: string; muted?: boolean }) {
  return (
    <div className={"rounded-lg px-2 py-1.5 " + (muted ? "bg-slate-50" : "bg-slate-100")}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate" title={note}>{label}</div>
      <div className={"font-semibold " + (value < 0 ? "text-red-600" : "text-bardin-ink")}>{fmtUSD(value)}</div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg px-2 py-1.5 bg-white border border-slate-200">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate">{label}</div>
      <div className="font-semibold text-bardin-ink">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

const CHIP_TONES = {
  amber: "bg-amber-100 text-amber-800",
  rose: "bg-rose-100 text-rose-700",
  slate: "bg-slate-200 text-slate-600",
} as const;

function Chip({ tone, children }: { tone: keyof typeof CHIP_TONES; children: ReactNode }) {
  return <span className={"text-[10px] font-medium px-1.5 py-0.5 rounded " + CHIP_TONES[tone]}>{children}</span>;
}
